import { Device } from '@/store/Station/constants';
import { AppliedFilter, FallBackdata, FilterCondition } from './types';
import { defaultFilterCondition, fallbackConstants, fieldOptions } from '@/constants/Fallback';
import { ConditionAction, FallbackFilterEnum } from '@/constants/FallbackEnum';
import { Scenario } from '@/store/Engine/FallbackScenarios';
import { DefaultBehaviour, EngineConfigStatus, FallbackBehaviour } from '@/store/Engine/constants';
import { message } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import {
  convertPressureToMetric,
  sortSetpointsByElementIndex,
  validateNewFallbackScenarioSetpoints,
} from '@/store/Engine/utils';
import { ProactiveEngineStateEnum, UnitMeasurementSystem } from '@/constants/enums';
import { EngineStore } from '@/store/Engine/EngineStore';
import { DeviceMetadata } from '@/store/Devices/constants';
import { ValidMetricTypes } from '../EngineVersionUtils';
import { MetricSetpoint } from '@/store/Engine/common';

export const compressorNamesFromIds = (compressorIds: string[], compressors: Device[]) => {
  if (compressorIds.length > 0) {
    return compressors
      .filter(compressor => compressorIds.includes(compressor.id))
      .map(compressor => compressor.name)
      .join(', ');
  }
  return [];
};

export const compressorNameFromID = (compressorId: string, compressors: Device[]): string => {
  return compressors.find(compressor => compressor.id === compressorId)?.name;
};

export const addCompressorNameInsetPointsIfNotExists = (setpoints, compressors: Device[]) => {
  return setpoints.map(setpoint => ({
    ...setpoint,
    compressorName: setpoint.compressorName || compressorNameFromID(setpoint.compressorId, compressors),
  }));
};

export const renderDescriptionForCompressors = (scenario: Scenario, compressors: Device[], t) => {
  let description = '';
  let compressorNames = [];
  if (scenario?.compressors?.length > 0) {
    compressorNames = compressors
      .filter(compressor => scenario?.compressors?.includes(compressor.id))
      .map(compressor => compressor.name);
  }

  if (scenario?.fallbackBehaviour === FallbackBehaviour.FlowPressureKPIMissing) {
    description =
      compressorNames?.length > 0
        ? `${t('compressorFailure')}: ${compressorNames.join(', ')}`
        : `${t('flowKPI')} ${t('AND')} ${t('pressureKPI')}`;
  } else if (scenario?.fallbackBehaviour === FallbackBehaviour.FlowKPIMissing) {
    description =
      compressorNames?.length > 0
        ? `${t('flowKPI')} ${t('AND')} ${t('compressorFailure')}: ${compressorNames.join(', ')}`
        : `${t('flowKPI')}`;
  } else if (scenario?.fallbackBehaviour === FallbackBehaviour.PressureKPIMissing) {
    description =
      compressorNames?.length > 0
        ? `${t('pressureKPI')} ${t('AND')} ${t('compressorFailure')}: ${compressorNames.join(', ')}`
        : `${t('pressureKPI')}`;
  }
  return description;
};

export const getActiveFallbackNumber = (commandsHistory): number => {
  if (!commandsHistory || commandsHistory.length === 0) return 0;
  const commandsHistoryCopy = JSON.parse(JSON.stringify(commandsHistory));
  const commandHistoryFallbackReason = commandsHistoryCopy[0]?.engineReason;
  if (commandHistoryFallbackReason) {
    const match = commandHistoryFallbackReason.match(/Fallback #(\d+)/);
    return match ? +match[1] : 0;
  }
  return 0;
};

export const addCondition = (conditions: FilterCondition[]): FilterCondition[] => {
  const newCondition = { ...defaultFilterCondition, id: Date.now() };
  return [...conditions, newCondition];
};

export const updateCondition = (
  conditions: FilterCondition[],
  id: number,
  key: keyof FilterCondition,
  val,
  setAppliedFilters?: (filters: FilterCondition[]) => void,
): FilterCondition[] => {
  const newConditions = conditions.map(cond =>
    cond.id === id
      ? key === fallbackConstants.field
        ? { ...cond, field: val, value: val === FallbackFilterEnum.CompressorFailure ? [] : '' }
        : { ...cond, [key]: val }
      : cond,
  );

  setAppliedFilters && setAppliedFilters(newConditions.filter(c => c.field));

  return newConditions;
};

export const removeCondition = (
  conditions: FilterCondition[],
  id: number,
  setAppliedFilters?: (filters: FilterCondition[]) => void,
): FilterCondition[] => {
  const newConditions = conditions.filter(cond => cond.id !== id);

  if (setAppliedFilters) {
    const validConditions = newConditions.filter(c => c.field);
    setAppliedFilters(validConditions);
  }

  return newConditions;
};

export const updateFilterCondition = (
  conditions: FilterCondition[],
  id: number,
  key: keyof FilterCondition,
  val,
  label?: string,
  compressorOptions?: { label: string; value: string }[],
): FilterCondition[] => {
  return conditions.map(cond => {
    if (cond.id !== id) {
      return cond;
    }

    if (key === fallbackConstants.field) {
      const resetVal = val === FallbackFilterEnum.CompressorFailure ? [] : '';
      const selectedField = fieldOptions.find(opt => opt.value === val);
      const fieldLabel = selectedField?.label || '';

      return {
        ...cond,
        field: val,
        value: resetVal,
        label: fieldLabel,
        compressorLabels: [],
      };
    }

    if (key === fallbackConstants.value && cond.field === FallbackFilterEnum.CompressorFailure && compressorOptions) {
      const selectedCompressorLabels = (val as string[])
        .map(compressorId => compressorOptions.find(opt => opt.value === compressorId)?.label)
        .filter(Boolean);

      return {
        ...cond,
        [key]: val,
        compressorLabels: selectedCompressorLabels,
      };
    }
    return { ...cond, [key]: val };
  });
};

export const getConditionLabelAsCompressorFailure = (cond: FilterCondition) => {
  if (cond.field === FallbackFilterEnum.CompressorFailure) {
    return true;
  }
  return false;
};

export const mapAppliedFiltersToDisplayText = (appliedFilters: AppliedFilter[], t): string => {
  return appliedFilters
    .map(filter => {
      if (filter.field === FallbackFilterEnum.FlowKPI) {
        return `${t('flowKPI')}`;
      }
      if (filter.field === FallbackFilterEnum.PressureKPI) {
        return `${t('pressureKPI')}`;
      }
      if (filter.field === FallbackFilterEnum.CompressorFailure) {
        return `${t('compressorFailure')}: ${(filter.compressorLabels as string[]).join(', ')}`;
      }
      return filter.label || filter.field;
    })
    .join(` ${t('and')} `);
};

export const appliedCompressorsValues = (appliedFilters: FilterCondition[]): string => {
  let compressors = '';
  if (appliedFilters.length > 0) {
    appliedFilters.forEach(filter => {
      if (filter.field === FallbackFilterEnum.CompressorFailure) {
        compressors += `${(filter.value as string[]).join(',')}`;
      }
    });
  }
  return compressors;
};

export const appliedCompressorsValuesArray = (appliedFilters: FilterCondition[]): string[] => {
  const compressors = [];
  if (appliedFilters.length > 0) {
    appliedFilters.forEach(filter => {
      if (filter.field === FallbackFilterEnum.CompressorFailure) {
        compressors.push(...(filter.value as string[]));
      }
    });
  }
  return compressors;
};

export const isFlowKPIFailed = (appliedFilters: FilterCondition[]): boolean => {
  if (appliedFilters.length > 0) {
    return appliedFilters.some(filter => filter.field === FallbackFilterEnum.FlowKPI);
  }
  return false;
};

export const isPressureKPIFailed = (appliedFilters: FilterCondition[]): boolean => {
  if (appliedFilters.length > 0) {
    return appliedFilters.some(filter => filter.field === FallbackFilterEnum.PressureKPI);
  }
  return false;
};

export const transformNewFallbackList = (
  newFallbackScenarios,
  commandsHistory,
  proactiveEngineState,
  fromVersionHistory,
) => {
  if (!newFallbackScenarios?.scenarios) return [];
  const activeFallbackNumber = getActiveFallbackNumber(commandsHistory);
  return newFallbackScenarios?.scenarios?.map(scenario => ({
    active:
      activeFallbackNumber === +scenario.name.substring(8) &&
      proactiveEngineState === ProactiveEngineStateEnum.Activated &&
      !fromVersionHistory,
    id: scenario?.id,
    title: scenario?.name?.replace('Scenario', 'Fallback'),
    description: scenario?.name?.replace('Scenario', 'Fallback'),
    scenario: {
      ...scenario,
      fallbackRules: scenario?.fallbackRules || [],
    },
  }));
};

export const getCompData = (selectedFallback, engineStore: EngineStore, sortedCompressors: DeviceMetadata[]) => {
  if (!selectedFallback) return [];
  const fallbackRules = selectedFallback.scenario?.fallbackRules;
  if (!fallbackRules?.length) return [];
  if (fallbackRules[0]?.defaultBehaviour === DefaultBehaviour.ExistingRule) {
    return engineStore?.setpointsConfiguration?.getSetpointsByPressureRange(fallbackRules[0]?.ruleParentKey) || [];
  } else if (fallbackRules[0]?.defaultBehaviour === DefaultBehaviour.CustomRule) {
    return sortSetpointsByElementIndex(fallbackRules[0]?.rules, sortedCompressors) || [];
  }
  return [];
};

export const getFilteredList = (newFallBackList: FallBackdata[], searchValue: string): FallBackdata[] => {
  let filtered = newFallBackList;
  if (searchValue) {
    const searchLower = searchValue.toLowerCase();
    filtered = filtered.filter(
      item => item.title.toLowerCase().includes(searchLower) || item.description.toLowerCase().includes(searchLower),
    );
  }
  return filtered;
};

export const handleAddFallbackSaveSuccess = async (
  appliedFilters: FilterCondition[],
  engineStore: EngineStore,
  configurationStatus: EngineConfigStatus,
  tvalue,
) => {
  const compressors = appliedCompressorsValues(appliedFilters);
  const flowKPIFailed = isFlowKPIFailed(appliedFilters);
  const pressureKPIFailed = isPressureKPIFailed(appliedFilters);
  await engineStore.getFallbackScenarios(
    configurationStatus,
    engineStore.pagination.page,
    engineStore.pagination.pageSize,
    compressors,
    flowKPIFailed,
    pressureKPIFailed,
    tvalue,
  );
  await engineStore.getEngineConfig(tvalue, true);
};

export const validationsForSavingFallback = (fallbackScenarios: Scenario[], payload: Scenario) => {
  if (fallbackScenarios.length === 0) {
    return '';
  }

  if (!payload.fallbackBehaviour) {
    return 'oneFallbackOptionShouldBeSelected';
  }
  if (
    payload.fallbackRules.some(
      rule =>
        !rule.defaultBehaviour ||
        (rule.defaultBehaviour === DefaultBehaviour.ExistingRule && !rule.ruleParentKey) ||
        (rule.defaultBehaviour === DefaultBehaviour.CustomRule && !rule.rules.length),
    )
  ) {
    return 'oneRuleShouldBeSelected';
  }
  return '';
};

export const checkForDuplicateScenarios = (
  fallbackScenarios: Scenario[],
  compressors: string[],
  validConditions: FilterCondition[],
) => {
  const fallbackBehaviour = getFallbackBehaviour(validConditions);
  return fallbackScenarios.some(scenario => {
    const scenarioCompressors = scenario.compressors || [];
    // Check for exact equality of compressor arrays (both directions)
    const compressorsMatch =
      compressors.length === scenarioCompressors.length &&
      compressors.every(compressor => scenarioCompressors.includes(compressor)) &&
      scenarioCompressors.every(compressor => compressors.includes(compressor));

    return compressorsMatch && scenario.fallbackBehaviour === fallbackBehaviour;
  });
};
export const checkIfAllCompressorsAreSelected = (
  compressors: string[],
  sortedCompressors: DeviceMetadata[],
): boolean => {
  const sortedCompressorsIds = sortedCompressors?.map(compressor => compressor.id);
  return (
    compressors.length === sortedCompressorsIds.length &&
    compressors.every(compressor => sortedCompressorsIds.includes(compressor))
  );
};

export interface HandleSaveScenarioParams {
  appliedFilters: FilterCondition[];
  rangeParentKey?: string | null;
  smartRulesData?;
  engineStore: EngineStore;
  devicesStore;
  tvalue;
  t: (key: string) => string;
  onSaveSuccess?: () => void;
  onClose: () => void;
}

export interface HandleUpdateScenarioParams {
  selectedFallback;
  currentTableData;
  tvalue;
  engineStore: EngineStore;
  appliedFilters: FilterCondition[];
  configurationStatus: EngineConfigStatus;
  setEditableTableData: (data) => void;
  devicesMetadata?: DeviceMetadata[];
  t?: (key: string) => string;
}

export interface HandleRemoveScenarioParams {
  selectedFallback;
  engineStore: EngineStore;
  tvalue;
  appliedFilters: FilterCondition[];
  configurationStatus: EngineConfigStatus;
}

export const handleSaveScenario = async ({
  appliedFilters,
  rangeParentKey,
  smartRulesData,
  engineStore,
  devicesStore,
  tvalue,
  t,
  onSaveSuccess,
  onClose,
}: HandleSaveScenarioParams) => {
  try {
    if (appliedFilters.length === 0) {
      message.error(t('pleaseSelectAtLeastOneFilter'));
      return;
    }
    const isMeasurementUnitImperial = engineStore?.unitMesurementSystem === UnitMeasurementSystem.Imperial;
    const compressorsToSave = appliedFilters
      .filter(filter => filter.field === FallbackFilterEnum.CompressorFailure)
      .flatMap(filter => filter.value as string[]);

    const fallbackBehaviour = getFallbackBehaviour(appliedFilters);

    const fallbackScenariosName = engineStore?.fallbackScenarioNames;
    const scenarioName = `Scenario ${fallbackScenariosName}`;

    if (rangeParentKey || smartRulesData) {
      let savePayload;

      if (!rangeParentKey && smartRulesData) {
        const sortedSmartRulesData = sortSetpointsByElementIndex(smartRulesData, devicesStore?.sortedCompressors);

        savePayload = {
          id: uuidv4(),
          name: scenarioName,
          fallbackBehaviour: fallbackBehaviour,
          compressors: compressorsToSave,
          fallbackRules: [
            {
              id: uuidv4(),
              defaultBehaviour: DefaultBehaviour.CustomRule,
              rules: sortedSmartRulesData?.map(rule => ({
                ...rule,
                load: isMeasurementUnitImperial ? convertPressureToMetric(rule?.load, tvalue) : rule?.load,
                unload: isMeasurementUnitImperial ? convertPressureToMetric(rule?.unload, tvalue) : rule?.unload,
                autoStop: isMeasurementUnitImperial ? convertPressureToMetric(rule?.autoStop, tvalue) : rule?.autoStop,
                vsdTarget: isMeasurementUnitImperial
                  ? convertPressureToMetric(rule?.vsdTarget, tvalue)
                  : rule?.vsdTarget,
                immediateStop: isMeasurementUnitImperial
                  ? convertPressureToMetric(rule?.immediateStop, tvalue)
                  : rule?.immediateStop,
                pressureDrop: isMeasurementUnitImperial
                  ? convertPressureToMetric(rule?.pressureDrop, tvalue)
                  : rule?.pressureDrop,
              })),
            },
          ],
        };
      } else if (rangeParentKey) {
        savePayload = {
          id: uuidv4(),
          name: scenarioName,
          fallbackBehaviour: fallbackBehaviour,
          compressors: compressorsToSave,
          fallbackRules: [
            {
              id: uuidv4(),
              ruleParentKey: rangeParentKey,
              defaultBehaviour: DefaultBehaviour.ExistingRule,
            },
          ],
        };
      }

      const validationMessage = validationsForSavingFallback(engineStore?.fallbackScenarios?.scenarios, savePayload);
      if (validationMessage !== '') {
        message.error(t(validationMessage));
        return;
      }

      await engineStore?.createAndUpdateNewFallbackScenario([savePayload]);
      message.success(t('fallbackScenarioSavedSuccessfully'));

      if (onSaveSuccess) {
        onSaveSuccess();
      }
      onClose();
    } else {
      message.error(t('invalidRangeParentKeyOrSmartRulesData'));
    }
  } catch (error) {
    message.error(t('failedToSaveFallbackScenarioPleaseTryAgain'));
  } finally {
    await engineStore.getEngineConfig(tvalue, true);
  }
};

export const handleUpdateScenario = async ({
  selectedFallback,
  currentTableData,
  tvalue,
  engineStore,
  appliedFilters,
  configurationStatus,
  setEditableTableData,
  devicesMetadata,
  t,
}: HandleUpdateScenarioParams) => {
  if (!selectedFallback) {
    return;
  }
  try {
    const isMeasurementUnitImperial = engineStore?.unitMesurementSystem === UnitMeasurementSystem.Imperial;
    const validationMessagesForOrderandPressureDrop = validateNewFallbackScenarioSetpoints(
      currentTableData,
      selectedFallback.scenario?.compressors,
      devicesMetadata,
    );
    if (validationMessagesForOrderandPressureDrop.length > 0) {
      message.error(t(validationMessagesForOrderandPressureDrop[0].message));
      return;
    }
    const updatedScenario = {
      ...selectedFallback.scenario,
      fallbackRules: selectedFallback.scenario?.fallbackRules?.map(rule => ({
        ...rule,
        rules: currentTableData.map(row => ({
          ...row,
          load: isMeasurementUnitImperial ? convertPressureToMetric(row.load, tvalue) : row.load,
          vsdTarget: isMeasurementUnitImperial ? convertPressureToMetric(row.vsdTarget, tvalue) : row.vsdTarget,
          unload: isMeasurementUnitImperial ? convertPressureToMetric(row.unload, tvalue) : row.unload,
          autoStop: isMeasurementUnitImperial ? convertPressureToMetric(row.autoStop, tvalue) : row.autoStop,
          modulate: row.modulate,
          bandConfiguration: row.bandConfiguration,
          immediateStop: isMeasurementUnitImperial
            ? convertPressureToMetric(row.immediateStop, tvalue)
            : row.immediateStop,
          pressureDrop: isMeasurementUnitImperial
            ? convertPressureToMetric(row?.pressureDrop, tvalue)
            : row.pressureDrop,
        })),
      })),
    };

    await engineStore.createAndUpdateNewFallbackScenario([updatedScenario]);
    setEditableTableData([]);

    const compressors = appliedCompressorsValues(appliedFilters);
    const flowKPIFailed = isFlowKPIFailed(appliedFilters);
    const pressureKPIFailed = isPressureKPIFailed(appliedFilters);
    await engineStore.getFallbackScenarios(
      configurationStatus,
      engineStore.pagination.page,
      engineStore.pagination.pageSize,
      compressors,
      flowKPIFailed,
      pressureKPIFailed,
      tvalue,
    );
  } catch (error) {
    console.error(error);
  } finally {
    await engineStore.getEngineConfig(tvalue, true);
  }
};

export const handleRemoveScenario = async ({
  selectedFallback,
  engineStore,
  tvalue,
  appliedFilters,
  configurationStatus,
}: HandleRemoveScenarioParams) => {
  if (!selectedFallback) {
    return;
  }
  try {
    await engineStore.deleteFallbackScenario([selectedFallback.scenario]);
    await engineStore.getEngineConfig(tvalue, true);
    const compressors = appliedCompressorsValues(appliedFilters);
    const flowKPIFailed = isFlowKPIFailed(appliedFilters);
    const pressureKPIFailed = isPressureKPIFailed(appliedFilters);
    await engineStore.getFallbackScenarios(
      configurationStatus,
      1,
      engineStore.pagination.pageSize,
      compressors,
      flowKPIFailed,
      pressureKPIFailed,
      tvalue,
    );
  } catch (error) {
    console.error('Failed to remove scenario:', error);
  } finally {
    await engineStore.getEngineConfig(tvalue, true);
  }
};

export const getMetricsByCompressor = (engineMetricSetpoints: MetricSetpoint[]) => {
  const result = {};
  Object.values(engineMetricSetpoints || {}).forEach((metric: { metricType: string; deviceId: string }) => {
    if (Object.values(ValidMetricTypes).includes(metric.metricType as ValidMetricTypes)) {
      if (!result[metric.deviceId]) {
        result[metric.deviceId] = [];
      }
      result[metric.deviceId].push(metric);
    }
  });
  return result;
};

export const getTablesToshow = setPointsConfiguration => {
  const tableNamesWithRuleParentKey = setPointsConfiguration?.map(setpoint => ({
    ruleParentKey: setpoint?.parentRangeKey,
    name: setpoint?.name,
  }));
  return tableNamesWithRuleParentKey?.map(setpoint => ({ id: setpoint?.ruleParentKey, name: setpoint?.name }));
};

export const validateFilters = (filters: FilterCondition[]) => {
  const filtersArray = [];
  for (const cond of filters) {
    if (cond.field === FallbackFilterEnum.CompressorFailure && (!cond.value || (cond.value as string[]).length === 0)) {
      return 'selectAtleastOneCompressor';
    }
    filtersArray.push(cond.field);
  }
  if (
    filtersArray.includes(FallbackFilterEnum.CompressorFailure) &&
    filtersArray.includes(FallbackFilterEnum.FlowKPI) &&
    filtersArray.includes(FallbackFilterEnum.PressureKPI)
  ) {
    return 'invalidScenario';
  }
  return '';
};

export const getFallbackBehaviour = (appliedFilters: FilterCondition[]) => {
  const appliedFiltersFields = appliedFilters.map(filter => filter.field);
  let fallbackBehaviour = FallbackBehaviour.FlowPressureKPIMissing;
  if (
    appliedFiltersFields.includes(FallbackFilterEnum.FlowKPI) &&
    appliedFiltersFields.includes(FallbackFilterEnum.PressureKPI)
  ) {
    fallbackBehaviour = FallbackBehaviour.FlowPressureKPIMissing;
  } else if (appliedFiltersFields.includes(FallbackFilterEnum.FlowKPI)) {
    fallbackBehaviour = FallbackBehaviour.FlowKPIMissing;
  } else if (appliedFiltersFields.includes(FallbackFilterEnum.PressureKPI)) {
    fallbackBehaviour = FallbackBehaviour.PressureKPIMissing;
  } else {
    fallbackBehaviour = FallbackBehaviour.FlowPressureKPIMissing;
  }
  return fallbackBehaviour;
};

export const handleConditionChange = (
  cond: FilterCondition,
  action: ConditionAction,
  val,
  updateConditionHandler: (id: number, key: keyof FilterCondition, val) => void,
  removeConditionHandler: (id: number) => void,
) => {
  if (action === ConditionAction.Remove) {
    removeConditionHandler(cond.id);
  } else {
    updateConditionHandler(cond.id, action, val);
  }
};
